import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import GameOptionsPage from "./pages/GameOptionsPage";
import MultiPlayerPage from "./pages/MultiPlayerPage";
import ChooseTopicsPage from "./pages/ChooseTopicsPage";
import PlayGamePage from "./pages/PlayGamePage";
import { UserProvider } from "./UserContext";
import LeaderboardPage from "./pages/LeaderboardPage";
import WeeklyLeaderboardPage from "./pages/WeeklyLeaderboardPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import Dock from "./components/Dock";
import HomePage from "./pages/HomePage";
import ContentMakerPage from "./pages/ContentMakerPage";
import TutorialPage from "./pages/TutorialPage";
import AnswerHistoryPage from "./pages/AnswerHistoryPage";
import AnswerDetailPage from "./pages/AnswerDetailPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ProfilePage from "./pages/ProfilePage";
import PracticePage from "./pages/PracticePage";
import SatTestPage from "./pages/SatTestPage";
import { InactivityProvider } from "./components/InactivityProvider";
import SATTestReportPage from "./pages/SATTestReportPage";
import ColyseusMultiplayerPage from "./pages/ColyseusMultiplayerPage";
import MultiplayerLobbyPage from "./pages/MultiplayerLobby";
import MultiplayerWaitingRoom from "./pages/MultiplayerWaitingRoom";
import MultiplayerGameRoom from "./pages/MultiplayerGameRoom";
import MultiplayerGameReport from "./pages/MultiplayerGameReport";
import SubscriptionPage from "./pages/SubscriptionPage";
import PricingPage from "./pages/PricingPage";
import ReferralPage from "./pages/ReferralPage";
import ContactUsPage from "./pages/ContactUsPage";
import AboutMetyPage from "./pages/AboutMetyPage";
import AboutGamePage from "./pages/AboutGamePage";
import SATPage from "./pages/SATPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import Footer from "./components/Footer";
import AnnouncementBar from "./components/AnnouncementBar";
import { AnnouncementBarProvider } from "./components/AnnouncementBarContext";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
};

const AppRoutes = () => {
  // Inner component to use useLocation inside Router
  const AppRoutesInner = () => {
    const location = useLocation();
    const hideNavbarRoutes = ["/sat-test"];
    const showNavbar = !hideNavbarRoutes.includes(location.pathname);
    const [score, setScore] = useState(0);
    return (
      <>
        <AnnouncementBar />
        {showNavbar && <Dock />}
        <div className="min-h-screen flex flex-col">
          <div className="flex-1">
            <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={
             <ProtectedRoute>
              <LoginPage />
             </ProtectedRoute>
             } />
          <Route path="/signup" element={
            <ProtectedRoute>
              <SignupPage />
            </ProtectedRoute>
          } />
          <Route path="/forgot-password" element={
            <ProtectedRoute>
              <ForgotPasswordPage />
            </ProtectedRoute>
          } />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/about-mety-technology" element={<AboutMetyPage />} />
          <Route path="/about-the-game" element={<AboutGamePage />} />
          <Route path="/sat" element={<SATPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />

          {/* Protected Routes */}
          <Route
            path="/options"
            element={
              <ProtectedRoute>
                <GameOptionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compete"
            element={
              <ProtectedRoute>
                <MultiPlayerPage />
              </ProtectedRoute>
            }
          />
          <Route path="/colyseus-multiplayer"
            element={
              <ProtectedRoute>
                <ColyseusMultiplayerPage />
              </ProtectedRoute>
            } />
          <Route
            path="/contentmaker"
            element={
              <ProtectedRoute>
                <ContentMakerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/solo"
            element={
              <ProtectedRoute>
                <TutorialPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/choose-topics"
            element={
              <ProtectedRoute>
                <ChooseTopicsPage score={score} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/play-game"
            element={
              <ProtectedRoute>
                <PlayGamePage score={score} setScore={setScore} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute>
                <LeaderboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/WeeklyLeaderboardPage"
            element={
              <ProtectedRoute>
                <WeeklyLeaderboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/answer-history"
            element={
              <ProtectedRoute>
                <AnswerHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/answer-history/:scoreId"
            element={
              <ProtectedRoute>
                <AnswerDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice"
            element={
              <ProtectedRoute>
                <PracticePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sat-test"
            element={
              <ProtectedRoute>
                <SatTestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sat-test-report"
            element={
              <ProtectedRoute>
                <SATTestReportPage />
              </ProtectedRoute>
            }
          />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          } />
          <Route path="/refer" element={
            <ProtectedRoute>
              <ReferralPage />
            </ProtectedRoute>
          } />

        <Route path="/colyseus-multiplayer"
            element={
              <ProtectedRoute>
                <ColyseusMultiplayerPage />
              </ProtectedRoute>
            } />
        
        <Route path="/lobby-multiplayer"
            element={
              <ProtectedRoute>
                <MultiplayerLobbyPage />
              </ProtectedRoute>
            } />
        <Route path="/multiplayer/waiting-room/:roomId"
            element={
              <ProtectedRoute>
                <MultiplayerWaitingRoom />
              </ProtectedRoute>
            } />
        <Route path="/multiplayer/game/:roomId"
            element={
              <ProtectedRoute>
                <MultiplayerGameRoom />
              </ProtectedRoute>
            } />
        <Route path="/multiplayer/report/:roomId"
            element={
              <ProtectedRoute>
                <MultiplayerGameReport />
              </ProtectedRoute>
            } />
            </Routes>
          </div>
          <Footer />
        </div>
      </>
    );
  };

  return (
    <UserProvider>
      <Router>
        <AnnouncementBarProvider>
          <InactivityProvider>
            <ScrollToTop />
            <AppRoutesInner />
          </InactivityProvider>
        </AnnouncementBarProvider>
      </Router>
    </UserProvider>
  );
};

export default AppRoutes;
